/**
 * Enter Test Mode
 *
 * Ends the conversation being left behind, records the agent profile to test and
 * reloads. The next boot reads the persisted slice, resolves the tester
 * transport and builds the test gateway — see
 * `configuration/resolveChatTransportDecision`.
 *
 * The config is validated by the caller, which is where a driver's bad input
 * has to be rejected: this runs after the widget bundle has been loaded to
 * reach the store, and loading it is too much to do on the strength of an
 * unchecked value.
 */

import { resetConversation } from '@/actions/session/resetConversation';
import type { ActionEnvironment } from '@/domain/actionEnvironment';
import type { TestModeConfig } from '@/domain/testMode';
import { reloadPage } from '@/lib/reloadPage';

export async function enterTestMode(env: ActionEnvironment, config: TestModeConfig): Promise<void> {
  env.logAction('testMode/enterTestMode', { orgId: config.orgId, profileId: config.profileId });

  // Both doors end the conversation they leave. The order is load-bearing here
  // for a different reason than on the way out: mutating first would persist
  // `active: true` and only then await a network logout, so an unload inside
  // that await records test mode with the provider session never ended.
  // Reset-first fails safe. (The logout itself is not gated on the mutation —
  // `resetConversation` reads the live `env.store.user`, and `testModeEntered`
  // does not touch it, unlike `testModeExited`, where the credential clear
  // genuinely is the gate.)
  await resetConversation(env);

  // The mutation replaces the slice wholesale, so a session id from a previous
  // entry cannot survive into the profile being entered now.
  env.store.testModeMutations.testModeEntered(config);
  reloadPage();
}
